import { useMemo } from 'react';
import classNames from 'classnames';

import { InputSize, parseWidthCssVar } from '@xemplo/input-utils';

import { SelectProps } from './select.types';

/**
 * Custom hook that generates CSS class names and styling properties for a select component.
 *
 * @returns Object containing computed class names and styles:
 * - `container`: Class names for the container element
 * - `select`: Class names for the select element
 * - `placeholder`: Class names for the placeholder element
 * - `label`: Class names for the label element
 * - `arrowIcon`: Class names for the arrow icon element
 * - `width`: Parsed width CSS variable
 *
 * @remarks
 * This hook uses `useMemo` to optimize performance by only recalculating
 * class names when props change.
 */
export function useSelectStyle(props: SelectProps) {
  return useMemo(() => {
    const {
      disabled,
      errorMessage,
      readOnly,
      inputSize = InputSize.Default,
      open,
    } = props;

    const isHiddenLabel =
      inputSize === InputSize.Compact || !props.label || props.label.trim() === '';
    const hasLeadingIcon = !!props.leadingIcon;

    const hasValue = props.value && props.value.length > 0;
    const width = parseWidthCssVar(props.width);

    const container = classNames(inputSize, {
      disabled,
      readOnly,
      invalid: !!errorMessage,
      open,
    });

    const select = classNames(inputSize, {
      'has-value': hasValue,
      'has-placeholder': !!props.placeholder,
      'has-label': !isHiddenLabel,
    });

    const placeholder = classNames('placeholder', {
      'has-placeholder': !!props.placeholder,
    });

    const label = classNames(inputSize, {
      hidden: isHiddenLabel,
      left: hasLeadingIcon,
      open,
      'has-placeholder': !!props.placeholder,
      'has-value': hasValue,
    });

    const arrowIcon = classNames('trailing', { open });

    return { container, select, placeholder, label, arrowIcon, width };
  }, [props]);
}
